// @flow

import * as React from 'react';

import classNames from '@ta/common.classnames';
import { connect } from 'react-redux';
import { Avatar } from '@ta/styleguide.avatar';
import { featureIsEnabled } from '@ta/common.features';
import { getTripUsersForDisplay, getTripPermissions } from '@ta/trips.trip-util';
import * as Types from '@ta/trips.trip-types';

import styles from './TripAvatars.less';

/**
 * Define the size of the avatars
 */
type AvatarSize = 'small' | 'default' | 'large' | 'xtra-large';

type AvatarProps = {|
  /** Trip to get the owner and avatars from */
  trip: Types.Trip,
  /** Control what size to make the avatars */
  avatarSize?: AvatarSize,
  /** Control what size to make the avatars when they are displayed on tablet+*/
  tabletAvatarSize?: AvatarSize,
  /** How many avatars should we show */
  maxAvatars?: number,
  /** Callback to show the collaboration menu. */
  showCollabMenu?: () => void,
  /** Custom callback when an avatar is clicked */
  onAvatarClick?: () => void,
  /** Custom callback when the friend icon is clicked */
  addFriendsButton?: number => React.Node,
|};

type InternalAvatarProps = {|
  ...AvatarProps,
  /** Is the component being displaying in an RTL language. */
  isRtl: boolean,
|};

const defaultProps = {
  avatarSize: 'default',
  tabletAvatarSize: undefined,
  isRtl: false,
  maxAvatars: 6,
  onAvatarClick: undefined,
  addFriendsButton: undefined,
};

/**
 * This component displays a list of overlapping avatars.
 */
const TripAvatars = (props: InternalAvatarProps) => {
  // fall back to false on these permissions. If the feature is not enabled we want them using the fallback behavior.
  const tripPermissions = getTripPermissions(props.trip, false);

  let showAddFriends = false;
  let showCollabMenu; // undefined to match the Avatar's onclick
  let includeHref = false;

  const tripHasCollabs = (props.trip.collaborators || []).length > 0;
  if (
    tripPermissions?.canAddCollaborators &&
    featureIsEnabled('trips_2018_collab') &&
    featureIsEnabled('trips_2018_writes')
  ) {
    showAddFriends = true;
    showCollabMenu = tripHasCollabs ? props.showCollabMenu : undefined;
    includeHref = !showCollabMenu;
  } else {
    // fall back behavior if something goes wrong.
    showAddFriends = false;
    includeHref = true;
  }

  const users = getTripUsersForDisplay(
    props.trip,
    props.maxAvatars || defaultProps.maxAvatars,
    0,
    props.isRtl,
  );

  const sizeClass = props.avatarSize ? props.avatarSize.toString() : styles.default;

  const MAX_USERS_TO_DISPLAY = 5;
  const shownUsers =
    users.length > MAX_USERS_TO_DISPLAY ? users.slice(0, MAX_USERS_TO_DISPLAY) : users;

  const onAvatarClick = () => {
    if (props.onAvatarClick) {
      props.onAvatarClick();
    }

    if (showCollabMenu) {
      showCollabMenu();
    }
  };

  return (
    <React.Fragment>
      <span className={classNames('ui_social_avatar_group', styles.avatarGroup, styles[sizeClass])}>
        {shownUsers.map(
          user =>
            user && (
              <Avatar
                key={`${user.id}_${props.trip.id}`}
                size={props.avatarSize}
                tabletSize={props.tabletAvatarSize}
                href={includeHref ? user?.route?.url : ''}
                withBorder
                photo={user.avatar}
                onClick={onAvatarClick}
                mobileTarget="_self"
              />
            ),
        )}
        {showAddFriends && props.addFriendsButton && props.addFriendsButton(users.length)}
      </span>
    </React.Fragment>
  );
};

TripAvatars.defaultProps = defaultProps;

export default connect<InternalAvatarProps, AvatarProps, _, _, _, _>(state => ({
  isRtl: state.i18n.isRtl,
}))(TripAvatars);
